// @ts-nocheck
import React, { useState } from 'react';
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getExpandedRowModel,
  SortingState,
  ExpandedState,
  useReactTable,
  Row,
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Search, Plus, ChevronDown, Users, Wrench } from 'lucide-react';
import { useDialogManager } from '@/hooks/useDialogManager';
import { Crew } from '@/types';
import { useCrews } from '@/hooks/api/useCrews';
import { CrewTablePagination } from './CrewTablePagination';
import { useCrewTableColumns } from '../hooks/useCrewTableColumns';
import { TABLE_STYLES } from '@/lib/tableStyles';
import { cn } from '@/lib/utils';
import { TablePagination } from '@/components/shared/TablePagination';
import { useSearchParams } from 'react-router';

const CrewDetails = ({ crew }: { crew: Crew }) => (
  <div className="px-4 py-3 bg-muted/20 border-t border-border/50">
    <div className="flex items-center gap-2 mb-2">
      <Wrench className="w-3 h-3 text-muted-foreground" />
      <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
        Skills
      </span>
    </div>
    {crew.skills?.length ? (
      <div className="flex flex-wrap gap-1.5">
        {crew.skills.map((skill) => (
          <Badge
            key={skill.id}
            variant="secondary"
            className="text-xs h-5 px-2 font-normal"
          >
            {skill.name}
          </Badge>
        ))}
      </div>
    ) : (
      <p className="text-xs text-muted-foreground">No skills assigned.</p>
    )}
  </div>
);

export const CrewTable = () => {
  const { openDialog } = useDialogManager();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const { crews, isLoading } = useCrews();
  const columns = useCrewTableColumns();

  const [searchParams] = useSearchParams();
  const statusFilter = searchParams.get('status');

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(
    statusFilter ? [{ id: 'operationalStatus', value: statusFilter }] : [],
  );

  const table = useReactTable({
    data: crews,
    columns,
    state: { sorting, globalFilter, expanded, columnFilters },
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onExpandedChange: setExpanded,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
  });

  const totalCrews = table.getFilteredRowModel().rows.length;
  const isFiltered = globalFilter.length > 0;

  return (
    <div className="space-y-5 p-4 md:p-6">
      {/* ── Header ───────────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
            <Users className="w-4 h-4 text-primary" />
          </div>
          <div>
            <h1 className="text-xl font-bold tracking-tight text-foreground leading-tight">
              Crews
            </h1>
            <p className="text-xs text-muted-foreground">
              {totalCrews} crew{totalCrews !== 1 ? 's' : ''}
              {isFiltered && ' matching search'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative w-full sm:w-56">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              placeholder="Search crews..."
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              className="pl-9 h-9 text-sm"
            />
            {isFiltered && (
              <button
                onClick={() => setGlobalFilter('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground hover:text-foreground"
              >
                ✕
              </button>
            )}
          </div>
          <Button
            onClick={() => openDialog('create-crew')}
            size="sm"
            className="gap-1.5 h-9 shrink-0"
          >
            <Plus className="h-3.5 w-3.5" /> New Crew
          </Button>
        </div>
      </div>

      {/* ── Table ────────────────────────────────────────────────────────── */}
      <div className={TABLE_STYLES.wrapper}>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((hg) => (
                <TableRow key={hg.id} className={TABLE_STYLES.headerRow}>
                  {hg.headers.map((header) => (
                    <TableHead
                      key={header.id}
                      style={{ width: header.getSize() }}
                      className={TABLE_STYLES.headerCell}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                    </TableHead>
                  ))}
                  <TableHead className="w-10" />
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <TableRow
                    key={i}
                    className="animate-pulse border-b border-border/50"
                  >
                    {columns.map((_, j) => (
                      <TableCell key={j} className={TABLE_STYLES.cell}>
                        <div
                          className="h-4 bg-muted rounded-md"
                          style={{ width: `${60 + ((j * 13) % 30)}%` }}
                        />
                      </TableCell>
                    ))}
                    <TableCell className={TABLE_STYLES.cell} />
                  </TableRow>
                ))
              ) : table.getRowModel().rows.length ? (
                table.getRowModel().rows.map((row) => (
                  <React.Fragment key={row.id}>
                    <TableRow
                      onClick={() => row.toggleExpanded()}
                      data-expanded={row.getIsExpanded()}
                      className={cn(TABLE_STYLES.bodyRow, 'cursor-pointer')}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell
                          key={cell.id}
                          className={TABLE_STYLES.cell}
                          onClick={(e) => {
                            if (
                              cell.column.id === 'actions' ||
                              cell.column.id === 'tickets'
                            )
                              e.stopPropagation();
                          }}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </TableCell>
                      ))}
                      <TableCell className={cn(TABLE_STYLES.cell, 'w-10')}>
                        <ChevronDown
                          className={cn(
                            'w-3.5 h-3.5 text-muted-foreground transition-transform duration-200',
                            row.getIsExpanded() && 'rotate-180',
                          )}
                        />
                      </TableCell>
                    </TableRow>
                    {row.getIsExpanded() && (
                      <TableRow className="hover:bg-transparent border-b border-border/50">
                        <TableCell colSpan={columns.length + 1} className="p-0">
                          <CrewDetails crew={row.original} />
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))
              ) : (
                <TableRow className="hover:bg-transparent">
                  <TableCell
                    colSpan={columns.length + 1}
                    className="h-44 text-center"
                  >
                    <div className="flex flex-col items-center gap-2.5">
                      <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                        <Users className="w-5 h-5 text-muted-foreground" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">
                          {isFiltered ? 'No results found' : 'No crews yet'}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {isFiltered
                            ? `No crews match "${globalFilter}"`
                            : 'Create your first crew to get started'}
                        </p>
                      </div>
                      {!isFiltered && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openDialog('create-crew')}
                          className="gap-1.5 h-8 text-xs mt-1"
                        >
                          <Plus className="w-3 h-3" /> New Crew
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <TablePagination table={table} noun="crew" />
    </div>
  );
};
